<template>
  <div class="about-page">
    <Top />

    <section class="page-hero" aria-label="About header">
      <div class="container page-hero-inner">
        <h1 class="page-hero-title">{{ heroTitle }}</h1>
      </div>
    </section>

    <section class="about" aria-label="About Mor Haim">
      <div class="container about-inner">
        <p v-if="loadError" class="about-hint">{{ loadError }}</p>
        <div v-if="loading" class="about-loading" role="status">Loading…</div>
        <template v-else>
          <div class="about-photo">
            <img class="about-photo-img" :src="displayPhoto" :alt="displayName" />
          </div>

          <div class="about-copy">
            <div class="about-head">
              <div class="about-name">{{ displayName }}</div>
              <div v-if="displayTitle" class="about-title-line">{{ displayTitle }}</div>
              <div class="about-rule" aria-hidden="true" />
            </div>
            <div class="about-quote-mark" aria-hidden="true">“</div>

            <div class="about-text">
              <div
                v-if="displayDescriptionHtml"
                class="about-html"
                v-html="displayDescriptionHtml"
              />
              <template v-else>
                <p v-for="(p, idx) in aboutParagraphs" :key="idx" class="about-p">
                  {{ p }}
                </p>
              </template>
            </div>
          </div>
        </template>
      </div>
    </section>

    <section class="philosophy" aria-label="Our philosophy">
      <div class="container philosophy-inner">
        <h2 class="section-title">OUR PHILOSOPHY</h2>

        <div class="philosophy-grid">
          <div v-for="item in philosophy" :key="item.title" class="philosophy-item">
            <img class="philosophy-icon" :src="item.icon" :alt="item.title" />
            <div class="philosophy-title">{{ item.title }}</div>
            <div class="philosophy-sub">{{ item.sub }}</div>
          </div>
        </div>
      </div>
    </section>

    <section class="cta-band">
      <div class="container cta-inner">
        <div class="cta-left">
          <h2 class="cta-title">AESTHETICS PRACTITIONER?</h2>
          <p class="cta-sub">
            Grow your business with DOSE and access new patients, forms, diary, reminders<br />
            and no show/late cancellation protection.
          </p>
        </div>
        <div class="cta-right">
          <button class="cta-btn" type="button" @click="goAbout">LEARN MORE</button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import Footer from '../components/footer.vue'
import Top from '../components/top.vue'
import { fetchPublicDoctors } from '../api/doctor'
import { resolveAssetUrl, resolveHtmlAssetUrls } from '../utils/asset'

export default {
  name: 'AboutPage',
  components: {
    Footer,
    Top,
  },
  data() {
    return {
      loading: true,
      loadError: '',
      doctors: [],
      defaultPhoto: require('../assets/images/yisheng.jpg'),
      aboutParagraphs: [
        'With over 10 years of nursing experience and more than four years dedicated to aesthetic medicine, Mor Haim brings a thoughtful and detail-oriented approach to every treatment. Her work is rooted in both clinical expertise and a genuine passion for helping patients feel more confident in their own skin.',
        'She specializes in natural, undetectable results—whether through neurotoxins, dermal fillers, or biostimulators. Her goal is never to change how someone looks, but to enhance what’s already there in a way that feels subtle, balanced, and refined.',
        'Known for her meticulous eye, precision, and gentle hand, Mor approaches each treatment with intention. Every detail—product choice, placement, and dosage—is carefully considered, allowing her skill and passion for aesthetics to work seamlessly together.',
        'She is committed to ongoing education, routinely attending advanced aesthetic trainings and conferences, and staying current with the latest technologies, techniques, and evidence-based practices.',
        'Her approach remains conservative and refined. The best results are the ones that go unnoticed—leaving patients looking refreshed, without appearing “treated.”',
      ],
      philosophy: [
        {
          icon: require('../assets/images/icon01.png'),
          title: 'Clinical Excellence',
          sub: 'Evidence-based treatments backed by science.',
        },
        {
          icon: require('../assets/images/icon02.png'),
          title: 'Personalized Care',
          sub: 'Every treatment plan is tailored to you.',
        },
        {
          icon: require('../assets/images/icon03.png'),
          title: 'Natural Results',
          sub: 'Enhancing your beauty while keeping it you.',
        },
      ],
    }
  },
  computed: {
    primary() {
      return this.doctors.length ? this.doctors[0] : null
    },
    heroTitle() {
      const name = (this.primary && this.primary.name) || 'MOR HAIM'
      return 'ABOUT ' + String(name).toUpperCase()
    },
    displayName() {
      return (this.primary && this.primary.name) || 'MOR HAIM'
    },
    displayTitle() {
      return (this.primary && this.primary.title) || ''
    },
    displayPhoto() {
      const url = this.primary && this.primary.avatarUrl
      return resolveAssetUrl(url) || this.defaultPhoto
    },
    displayDescriptionHtml() {
      const html = this.primary && this.primary.description
      if (!html || !String(html).trim()) return ''
      return resolveHtmlAssetUrls(html)
    },
  },
  mounted() {
    fetchPublicDoctors()
      .then((list) => {
        this.doctors = list
      })
      .catch((e) => {
        this.loadError = (e && e.message) || '无法加载医师信息，已展示默认内容'
        this.doctors = []
      })
      .finally(() => {
        this.loading = false
      })
  },
  methods: {
    goAbout() {
      this.$router.push({ name: 'about' }).catch((err) => {
        if (err && err.name !== 'NavigationDuplicated') throw err
      })
    },
  },
}
</script>

<style scoped>
.about-page {
  min-height: 100vh;
  background: #ffffff;
  color: #0c0b06;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-hero {
  background: #f4f3ef;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.page-hero-inner {
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-hero-title {
  margin: 0;
  font-size: 16px;
  letter-spacing: 0.16em;
  font-weight: 500;
  color: #0c0b06;
}

.about {
  background: #ffffff;
}

.about-inner {
  padding: 54px 0 34px;
  display: grid;
  grid-template-columns: 460px 1fr;
  gap: 56px;
  align-items: start;
}

.about-hint {
  grid-column: 1 / -1;
  margin: 0;
  color: #c45656;
  font-size: 13px;
}

.about-loading {
  grid-column: 1 / -1;
  padding: 24px 0;
  color: #0c0b06;
  font-size: 13px;
}

.about-photo {
  background: transparent;
  border: none;
  padding: 0;
}

.about-photo-img {
  width: 100%;
  display: block;
  height: auto;
  object-fit: cover;
  border-radius: 2px;
}

.about-copy {
  position: relative;
  padding-top: 8px;
  max-width: 640px;
}

.about-head {
  display: block;
}

.about-name {
  font-size: 30px;
  letter-spacing: 0.06em;
  font-weight: 700;
  color: #0c0b06;
}

.about-title-line {
  margin-top: 8px;
  font-size: 12px;
  letter-spacing: 0.08em;
  color: #0c0b06;
}

.about-rule {
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  width: 100%;
  margin-top: 12px;
}

.about-quote-mark {
  position: relative;
  left: -20px;
  margin-top: 30px;
  font-size: 36px;
  color: #0c0b06;
  line-height: 1;
}

.about-text {
  padding-left: 0px;
}

.about-p {
  margin: 0 0 16px;
  font-size: 12px;
  line-height: 1.85;
  color: #0c0b06;
}

.about-html >>> p {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.85;
  color: #0c0b06;
}

.about-html >>> img {
  max-width: 100%;
  height: auto;
}

.philosophy {
  background: #ffffff;
  padding: 18px 0 52px;
}

.section-title {
  margin: 0;
  text-align: center;
  font-size: 16px;
  letter-spacing: 0.16em;
  font-weight: 500;
  color: #0c0b06;;
}

.philosophy-grid {
  margin-top: 36px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 44px;
  align-items: start;
}

.philosophy-item {
  text-align: center;
}

.philosophy-icon {
  width: 34px;
  height: 34px;
  object-fit: contain;
  opacity: 0.95;
}

.philosophy-title {
  margin-top: 14px;
  font-size: 11px;
  color: #0c0b06;
  font-weight: 500;
}

.philosophy-sub {
  margin-top: 12px;
  font-size: 10px;
  line-height: 1.6;
  color: #0c0b06;
}

.cta-band {
  padding: 44px 0;
  background: #f4f3ef;
}

.cta-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
}

.cta-title {
  margin: 0;
  font-size: 18px;
  letter-spacing: 0.08em;
  font-weight: 600;
}

.cta-sub {
  margin: 10px 0 0;
  color: #0c0b06;
  font-size: 12px;
  line-height: 1.7;
}

.cta-btn {
  height: 36px;
  min-width: 150px;
  padding: 0 26px;
  border: none;
  border-radius: 0;
  background: #b08a4a;
  color: #fff;
  font-size: 11px;
  letter-spacing: 0.14em;
  cursor: pointer;
}

.cta-btn:hover {
  filter: brightness(0.96);
}

@media (max-width: 1000px) {
  .about-inner {
    grid-template-columns: 360px 1fr;
    gap: 32px;
  }

  .philosophy-grid {
    gap: 28px;
  }
}

@media (max-width: 720px) {
  .container {
    padding: 0 12px;
  }
  .about-quote-mark{
    left:0
  }

  .page-hero-inner {
    height: 96px;
  }

  .about-inner {
    grid-template-columns: 1fr;
    gap: 22px;
    padding: 30px 16px 0 16px;
  }

  .about-copy {
    max-width: none;
  }

  .philosophy-grid {
    grid-template-columns: 1fr;
    gap: 26px;
  }

  .cta-inner {
    flex-direction: column;
    align-items: flex-start;
  }

  .cta-btn {
    width: 100%;
    max-width: 220px;
  }
}
</style>
